import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Eye, EyeOff, ArrowUp, SlidersHorizontal, Info } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import AdultHeroStrip from '../components/dashboard/AdultHeroStrip';
import useAdultMode from '../hooks/useAdultMode';
import '../styles/adult-mode.css';
import OceanAssessment from '../components/ocean/OceanAssessment';
import MandatoryRegistrationGate from '../components/ocean/MandatoryRegistrationGate';
import HeroSection from '../components/dashboard/HeroSection';
import WelcomeCard from '../components/dashboard/WelcomeCard';
import MyAvAsCard from '../components/dashboard/MyAvAsCard';
import RecentConversationsCard from '../components/dashboard/RecentConversationsCard';
import MoodTrackerCard from '../components/dashboard/MoodTrackerCard';
import CurrentStreakCard from '../components/dashboard/CurrentStreakCard';
import BadgesCard from '../components/dashboard/BadgesCard';
import ActivityFeedCard from '../components/dashboard/ActivityFeedCard';
import NotificationsCard from '../components/dashboard/NotificationsCard';
import MemorialAvAsCard from '../components/dashboard/MemorialAvAsCard';
import SubscriptionCard from '../components/dashboard/SubscriptionCard';
import SocialConnectionsCard from '../components/dashboard/SocialConnectionsCard';
import SocialScoreCard from '../components/dashboard/SocialScoreCard';
import CharterCard from '../components/dashboard/CharterCard';
import DualConsentBanner from '../components/dashboard/DualConsentBanner';
import TeenCharterCelebration from '../components/dashboard/TeenCharterCelebration';
import PlanUnlockCelebration from '../components/dashboard/PlanUnlockCelebration';
import FirstSessionGuide from '../components/dashboard/FirstSessionGuide';
import RealWorldChallengeCard from '../components/dashboard/RealWorldChallengeCard';
import JourneyChallengeCard from '../components/dashboard/JourneyChallengeCard';
import PathwaysBadgeCard from '../components/dashboard/PathwaysBadgeCard';
import PausedOverlay from '../components/dashboard/PausedOverlay';
import ReadinessScoreCard from '../components/dashboard/ReadinessScoreCard';
import AffirmationCard from '../components/dashboard/AffirmationCard';
import MoodInsightsCard from '../components/dashboard/MoodInsightsCard';
import AvAEvolutionCard from '../components/dashboard/AvAEvolutionCard';
import ConfidenceReplayCard from '../components/dashboard/ConfidenceReplayCard';
import QuestOfTheWeekCard from '../components/dashboard/QuestOfTheWeekCard';
import HeartsStakingCard from '../components/dashboard/HeartsStakingCard';
import LimitedDropCard from '../components/dashboard/LimitedDropCard';
import MoodStreakGardenCard from '../components/dashboard/MoodStreakGardenCard';
import FamilyChallengeCard from '../components/dashboard/FamilyChallengeCard';
import AvaMissesYouCard from '../components/dashboard/AvaMissesYouCard';
import ConversationFlashbackCard from '../components/dashboard/ConversationFlashbackCard';
import PeerInviteCard from '../components/dashboard/PeerInviteCard';
import ProactivePodiumCard from '../components/dashboard/ProactivePodiumCard';
import AskRebeccaCard from '../components/dashboard/AskRebeccaCard';
import GoldChampionMarquee from '../components/dashboard/GoldChampionMarquee';
import DashboardCardTour from '../components/dashboard/DashboardCardTour';
import InfoTooltipWrapper from '../components/dashboard/InfoTooltipWrapper';
import '../styles/dashboard-readability.css';
import MyReferencesCard from '../components/dashboard/MyReferencesCard';
import IdeasCard from '../components/dashboard/IdeasCard';
import StreakCard from '../components/engagement/StreakCard';
import StreakFreezeCard from '../components/engagement/StreakFreezeCard';
import DailyLoginReward from '../components/engagement/DailyLoginReward';
import ReferralLeaderboard from '../components/engagement/ReferralLeaderboard';
import SessionReminderCard from '../components/engagement/SessionReminderCard';
import WeeklyWrapUp from '../components/engagement/WeeklyWrapUp';
import FortuneCookie from '../components/engagement/FortuneCookie';
import MilestoneCelebration from '../components/engagement/MilestoneCelebration';
import AchievementUnlocked from '../components/engagement/AchievementUnlocked';
import SurpriseDropModal from '../components/engagement/SurpriseDropModal';
import FriendChallengeCard from '../components/dashboard/FriendChallengeCard';
import OnboardingTour from '../components/onboarding/OnboardingTour';  // noqa: kept for follow-up sidebar tour after FirstSessionGuide
import CompleteProfileWizard from '../components/onboarding/CompleteProfileWizard';
import WelcomeTour from '../components/onboarding/WelcomeTour';
import SocialProofNudge from '../components/onboarding/SocialProofNudge';
import QuickActionsWidget from '../components/onboarding/QuickActionsWidget';
import ProfileCompletionBar from '../components/onboarding/ProfileCompletionBar';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import DashboardCover from '../components/DashboardCover';
import SecureVaultCard from '../components/dashboard/SecureVaultCard';
import ReadOnlyBanner from '../components/account/ReadOnlyBanner';

// ─── Sprint 207g — Layout pool architecture ──────────────────────────────────
//
// The teen dashboard has FOUR HARD-CODED ANCHORS rendered above any pool —
// they are NEVER customisable and never appear in the Customise modal:
//
//   1. Main Greeting + Today's Action Buttons   (lines ~273-285)
//   2. Active Charter Banner                    (TeenCharterCelebration, ~292)
//   3. Your Secure Vault                        (SecureVaultCard, ~322)
//   4. Quick Actions + Profile Completion       (~350)
//
// Below those, two pools feed the masonry container:
//
//   • FIXED_CARDS    — always rendered, hover-X is NOT shown, can't be hidden.
//   • OPTIONAL_CARDS — user toggles via the Customise modal. Each entry must
//                     have a `defaultVisible` flag — Sprint 207h replaces the
//                     prior "array of enabled IDs" with a strict visibility
//                     dictionary so no card can ever go off-the-grid.
//
// Both pools render through ONE css-columns masonry so removing/adding any
// optional card auto-rebalances the layout (no trailing whitespace).
// ─────────────────────────────────────────────────────────────────────────────
const FIXED_CARDS = [
  { id: 'my-avas', component: MyAvAsCard, title: 'My Trainers' },
  { id: 'journey-challenge', component: JourneyChallengeCard, title: 'My Challenge Journey' },
  { id: 'real-world-challenge', component: RealWorldChallengeCard, title: "Today's Challenge" },
  { id: 'recent-conversations', component: RecentConversationsCard, title: 'Recent Conversations' },
  { id: 'mood-tracker', component: MoodTrackerCard, title: 'Mood Tracker' },
];

// ─── Sprint 207h — Optional card master registry ─────────────────────────────
// Every customisable card must be registered here with a STABLE, UNIQUE key.
// Adding a card to this map is the ONLY way it can appear in the masonry
// or the Customise modal. `defaultVisible: true` means the card is shown on
// a brand-new dashboard; the user can hide it via the X icon and bring it
// back via the Customise modal toggle. Seasonal/promotional cards (e.g. the
// "Zen Master" / "Limited Edition" drop rendered inside LimitedDropCard) are
// gated under a single registration — content rotates inside the component
// but the registration stays stable so the visibility flag never gets lost.
// ─────────────────────────────────────────────────────────────────────────────
const OPTIONAL_CARDS = {
  // Sprint 207i — gamified challenge blocks lead. Account details (Subscription)
  // pushed to the very bottom so the dashboard reads play-first, billing-last.
  'quest-of-the-week':      { component: QuestOfTheWeekCard,       title: 'Quest of the Week',     description: "This week's headline mission",                          defaultVisible: true  },
  'peer-invite':            { component: PeerInviteCard,           title: 'Invite a Friend',       description: 'Earn a Support Trophy NFT when a friend completes a challenge', defaultVisible: true  },
  'proactive-podium':       { component: ProactivePodiumCard,      title: 'Proactive Champions',   description: "This week's Gold / Silver / Bronze podium — could be you next",   defaultVisible: true  },
  'ask-rebecca':            { component: AskRebeccaCard,           title: 'Ask Rebecca',           description: 'A new STEM question every day — Rebecca will coach you through it', defaultVisible: true  },
  'my-references':          { component: MyReferencesCard,         title: 'My References',         description: 'The last few links and images your AvAs saved with you',        defaultVisible: true  },
  'limited-drop':           { component: LimitedDropCard,          title: 'Zen Master (Limited Drop)', description: 'Weekly limited-edition trophy drops',                defaultVisible: true  },
  'ava-evolution':          { component: AvAEvolutionCard,         title: 'AvA Evolution',         description: 'How your AvA is growing alongside you',                 defaultVisible: true  },
  'mood-streak-garden':     { component: MoodStreakGardenCard,     title: 'Mood Garden',           description: 'Watch your mood-streak garden grow',                    defaultVisible: false },
  'pathways':               { component: PathwaysBadgeCard,        title: 'My Badge Pathways',     description: 'Track your badge progress and mini-journeys',           defaultVisible: false },
  'current-streak':         { component: CurrentStreakCard,        title: 'Current Streak',        description: 'See your daily practice streak',                        defaultVisible: false },
  'badges':                 { component: BadgesCard,               title: 'Celebrations',          description: 'View earned badges and achievements',                   defaultVisible: false },
  'social-score':           { component: SocialScoreCard,          title: 'Social Score',          description: 'Monitor your social engagement score',                  defaultVisible: false },
  'social-connections':     { component: SocialConnectionsCard,    title: 'Real Connections',     description: 'Track your real-world social connections',              defaultVisible: false },
  'activity-feed':          { component: ActivityFeedCard,         title: 'Activity Feed',         description: 'See recent activity across the platform',               defaultVisible: false },
  'notifications':          { component: NotificationsCard,        title: 'Notifications',         description: 'View your pending notifications',                       defaultVisible: false },
  'charter':                { component: CharterCard,              title: 'Charter Doc',      description: 'Your signed HiBFF Commitment Charter',                  defaultVisible: false },
  'memorial-avas':          { component: MemorialAvAsCard,         title: 'Memorial AvAs',         description: 'Honour and remember loved ones',                        defaultVisible: false },
  'mood-insights':          { component: MoodInsightsCard,         title: 'Mood Insights',         description: 'AI-powered analysis of your mood patterns',             defaultVisible: false },
  'confidence-replay':      { component: ConfidenceReplayCard,     title: 'Confidence Replay',     description: "Your best session highlights — shareable!",            defaultVisible: false },
  'hearts-staking':         { component: HeartsStakingCard,        title: 'Hearts Staking',        description: "Invest in a friend's growth and earn bonus Hearts",     defaultVisible: false },
  'family-challenge':       { component: FamilyChallengeCard,      title: 'Family Challenge',      description: 'Weekly shared challenges with your parent or teen',     defaultVisible: false },
  'conversation-flashback': { component: ConversationFlashbackCard, title: 'Flashbacks',           description: "On This Day — see how much you've grown",               defaultVisible: false },
  'referral-leaderboard':   { component: ReferralLeaderboard,      title: 'Referral Champions',    description: 'Top referrers this month with crowns and medals',       defaultVisible: false },
  'ideas-inbox':            { component: IdeasCard,                title: 'Got an Idea?',          description: 'Suggest features — chosen ideas earn an NFT',           defaultVisible: true  },
  'subscription':           { component: SubscriptionCard,         title: 'Subscription',          description: 'Your plan, renewal date and upgrade options',          defaultVisible: true  },
};

// Challenge-related cards hidden for adults who opt out of challenges.
const CHALLENGE_CARD_IDS = ['journey-challenge', 'real-world-challenge', 'family-challenge'];

// Strict visibility dictionary baseline — every key in OPTIONAL_CARDS is
// present with an explicit true/false. Saved + loaded as `card_visibility`.
const DEFAULT_CARD_VISIBILITY = Object.fromEntries(
  Object.entries(OPTIONAL_CARDS).map(([id, cfg]) => [id, Boolean(cfg.defaultVisible)])
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cardVisibility, setCardVisibility] = useState(DEFAULT_CARD_VISIBILITY);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showOcean, setShowOcean] = useState(false);
  const [oceanMandatory, setOceanMandatory] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showProfileWizard, setShowProfileWizard] = useState(false);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [coverRevealed, setCoverRevealed] = useState(false);
  const [onboardingStage, setOnboardingStage] = useState({ stage: 'pre_unlock', challenges_completed: 0, threshold: 10, simplified_dashboard: true, next_unlock_in: 10 });
  // Adult track (Oct 2026): adults may opt out of weekly challenges.
  const isAdultAccount = !!(user && user.age_band && user.age_band !== 'teen');
  const [challengesOptIn, setChallengesOptIn] = useState(true);
  useEffect(() => { if (user) setChallengesOptIn(user.challenges_opt_in !== false); }, [user]);
  const challengesOff = isAdultAccount && !challengesOptIn;
  const toggleChallenges = async () => {
    const next = !challengesOptIn;
    setChallengesOptIn(next);
    try {
      await apiClient.request('/api/users/me', { method: 'PATCH', body: JSON.stringify({ challenges_opt_in: next }) });
    } catch (e) {
      setChallengesOptIn(!next);
    }
  };

  // Phase A + B — adult-mode gate. Flips theme + hides family widgets when
  // the account is young_adult/adult OR the persona was set to 'adult' on
  // signup. Single source of truth lives in useAdultMode.
  const isAdultMode = useAdultMode();

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadPreferences();
    // Check pause status
    apiClient.request('/api/users/subscription/status')
      .then(data => { if (data.subscription_status === 'paused') setIsPaused(true); })
      .catch(() => {});
  }, []);

  // OCEAN retake gate REMOVED (founder directive): teens who already
  // completed the 22 questions must land on the dashboard directly —
  // never a second question flow. The ONLY gate is the mandatory
  // journey-less one below.

  // Check if new user (no sessions yet). Was previously calling the
  // non-existent /api/conversations (404 noise in DevTools). The proper
  // signal for "has the teen started practising?" is the sessions list.
  useEffect(() => {
    const checkNewUser = async () => {
      try {
        const res = await apiClient.request('/api/sessions');
        if (!res || (Array.isArray(res) && res.length === 0)) {
          setIsNewUser(true);
        }
      } catch {
        // If API fails, don't show welcome card
      }
    };
    checkNewUser();
  }, []);

  // Mandatory 22-question registration (founder directive): teens without a
  // journey are gated into the original MayaProfileChat flow (OpenAI voice,
  // 12 preferences + 10 OCEAN) — the questions cannot be skipped and
  // registration isn't complete until they're answered.
  useEffect(() => {
    if (!user) return undefined;
    const exempt = ['admin', 'sub_admin', 'developer', 'school_admin', 'teacher', 'corporate_admin', 'manager', 'parent'];
    if (exempt.includes(user.role)) return undefined;
    // Accounts whose registration is already complete (or provisioned via a
    // gifted plan) must never be re-gated into Maya registration just because
    // no school journey has been assigned yet.
    if (user.onboarding_complete || user.is_gifted_subscription) return undefined;
    let cancelled = false;
    apiClient.request('/api/schools/journey/current')
      .then((cur) => {
        if (!cancelled && cur && cur.has_journey === false) {
          setOceanMandatory(true);
          setShowOcean(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Fetch onboarding stage (pre/post Challenge-10 unlock for simplified dashboard)
  useEffect(() => {
    apiClient.request('/api/onboarding/stage')
      .then(setOnboardingStage)
      .catch(() => {});
  }, []);

  // Show Complete Profile wizard for OAuth users missing DOB.
  // Once shown in a session, do NOT re-trigger when the user clicks Home or
  // re-enters the dashboard — the user may have intentionally deferred filling
  // it in. They can always finish via the Profile Completion bar / their
  // Profile page. (Pre-stabilisation regression: clicking Home re-popped the
  // modal, hijacking navigation.)
  useEffect(() => {
    if (
      user &&
      !user.dob &&
      !user.onboarding_complete &&
      !localStorage.getItem('hibff_profile_wizard_done') &&
      !sessionStorage.getItem('hibff_profile_wizard_shown_session')
    ) {
      setShowProfileWizard(true);
      sessionStorage.setItem('hibff_profile_wizard_shown_session', 'true');
    }
  }, [user]);

  // Show Welcome Tour for first-time users — but ONLY after the dashboard
  // cover has rolled up (otherwise tooltips appear on top of the cover).
  useEffect(() => {
    if (!coverRevealed) return;
    if (showProfileWizard) return;
    if (localStorage.getItem('hibff_welcome_tour_done')) return;
    const timer = setTimeout(() => setShowWelcomeTour(true), 600);
    return () => clearTimeout(timer);
  }, [showProfileWizard, coverRevealed]);

  const loadPreferences = async () => {
    try {
      const prefs = await apiClient.getDashboardPreferences();
      // Sprint 207h — strict dictionary state. Build a complete visibility
      // map by merging the defaults with whatever the backend has stored.
      // This guarantees that EVERY registered card is represented, even if
      // the user has never customised, or if new cards have been added
      // since their last save.
      const next = { ...DEFAULT_CARD_VISIBILITY };
      if (prefs && prefs.card_visibility && typeof prefs.card_visibility === 'object') {
        // New format — direct dictionary.
        Object.entries(prefs.card_visibility).forEach(([id, visible]) => {
          if (id in DEFAULT_CARD_VISIBILITY) next[id] = Boolean(visible);
        });
      } else if (prefs && Array.isArray(prefs.enabled_optional)) {
        // Legacy format — backward compat with the old array. Cards present
        // in the array are visible; cards missing AND not in defaults are
        // hidden; cards in defaults but missing from the array are hidden
        // (user explicitly hid them in a prior session).
        if (prefs.enabled_optional.length > 0) {
          Object.keys(DEFAULT_CARD_VISIBILITY).forEach((id) => {
            next[id] = prefs.enabled_optional.includes(id);
          });
        }
      }
      setCardVisibility(next);
    } catch {
      // API unreachable — keep the defaults dictionary.
      setCardVisibility(DEFAULT_CARD_VISIBILITY);
    }
  };

  const savePreferences = useCallback(async (visibility) => {
    try {
      // Persist both the new dictionary AND the legacy array so any other
      // consumer (or older client tab) still sees a sane snapshot.
      const enabledArray = Object.entries(visibility)
        .filter(([, v]) => v)
        .map(([id]) => id);
      await apiClient.saveDashboardPreferences({
        card_order: FIXED_CARDS.map((c) => c.id),
        hidden_cards: [],
        card_visibility: visibility,    // new canonical key
        enabled_optional: enabledArray, // legacy mirror
        theme: 'default',
      });
    } catch {
      console.error('Failed to save preferences');
    }
  }, []);

  // Hide a card → visibility[id] = false. Show a card → visibility[id] = true.
  // Toggle from the modal flips the current value. Hover-X on the dashboard
  // always sets to false explicitly (no flip — clicking X on a hidden card
  // would otherwise re-show it).
  const setCardVisible = useCallback((cardId, visible) => {
    // Only act on registered cards — unknown IDs are silently ignored so
    // legacy data can never corrupt the dictionary.
    if (!(cardId in DEFAULT_CARD_VISIBILITY)) return;
    setCardVisibility((prev) => {
      if (prev[cardId] === visible) return prev;
      const next = { ...prev, [cardId]: visible };
      savePreferences(next);
      return next;
    });
  }, [savePreferences]);

  const toggleOptionalCard = useCallback((cardId) => {
    if (!(cardId in DEFAULT_CARD_VISIBILITY)) return;
    setCardVisibility((prev) => {
      const next = { ...prev, [cardId]: !prev[cardId] };
      savePreferences(next);
      return next;
    });
  }, [savePreferences]);

  const hideCardFromX = useCallback((cardId) => setCardVisible(cardId, false), [setCardVisible]);

  return (
    <MandatoryRegistrationGate user={user}>
    <DashboardLayout>
      <div className={`teen-dash-root ${isAdultMode ? 'hibff-adult-mode' : ''}`.trim()} data-testid={isAdultMode ? 'dashboard-adult-mode' : 'dashboard-teen-mode'}>
      {/* Dashboard Cover */}
      <DashboardCover
        orgName="HiBFF"
        logoUrl="/hibff-logo.png"
        subtitle={`Welcome back, ${user?.name?.split(' ')[0] || 'there'}`}
        accentColor="#F5831F"
        onRevealed={() => setCoverRevealed(true)}
      />

      {isPaused && <PausedOverlay />}

      {/* Complete Profile Wizard (OAuth users) */}
      {showProfileWizard && (
        <CompleteProfileWizard
          onComplete={() => setShowProfileWizard(false)}
          onSkip={async () => {
            // Persist the skip server-side so the wizard doesn't re-pop on
            // a different browser, device, or after a localStorage clear.
            // This was a real bug: returning users were being trapped in
            // the wizard on every new session.
            setShowProfileWizard(false);
            localStorage.setItem('hibff_profile_wizard_done', 'true');
            try {
              await apiClient.request('/api/users/me', {
                method: 'PUT',
                body: JSON.stringify({ onboarding_complete: true }),
              });
            } catch (_e) {
              // localStorage will keep them out for THIS device at least
            }
          }}
        />
      )}

      {/* Welcome Tour (first-time users) */}
      {showWelcomeTour && (
        <WelcomeTour onComplete={() => setShowWelcomeTour(false)} />
      )}

      {/* Social Proof Nudge */}
      <SocialProofNudge />

      {/* OCEAN gate renders only AFTER the cover has been revealed so the
          two overlays never stack and steal each other's clicks. */}
      {showOcean && coverRevealed && (
        <OceanAssessment
          mandatory={oceanMandatory}
          onComplete={() => {
            localStorage.setItem('hibff_ocean_postponed_at', String(Date.now()));
            setShowOcean(false);
            if (oceanMandatory) {
              setOceanMandatory(false);
              window.location.reload();
            } else {
              navigate('/profile');
            }
          }}
          onPostpone={() => {
            localStorage.setItem('hibff_ocean_postponed_at', String(Date.now()));
            setShowOcean(false);
          }}
        />
      )}

      <HeroSection onCustomize={() => setShowAddPanel(true)} />

      {/* Phase A · #1 — split hero (adult mode): Next High-Stakes Conversation
          + Secure Vault side by side, one decision block instead of two. */}
      {isAdultMode ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="split-hero">
          <AdultHeroStrip />
          <SecureVaultCard />
        </div>
      ) : null}

      {/* Phase D — FirstSessionGuide now renders for BOTH personas; the
          component itself swaps to ADULT_BUBBLES copy when isAdultMode. */}
      <FirstSessionGuide />

      {/* Charter celebration + dual-consent banner stay teen-only. */}
      {!isAdultMode && <PlanUnlockCelebration />}
      {!isAdultMode && <TeenCharterCelebration />}
      {!isAdultMode && <DualConsentBanner />}

      {/* Read-only banner (Sprint 198) — shows above everything when the
          user's account is paused (90-day or indefinite "memory lane"). */}
      <ReadOnlyBanner />

      {/* 🔐 Secure Vault — teen hero card (in adult mode it lives in the
          split hero above, so don't render it twice). */}
      {!isAdultMode && (
      <InfoTooltipWrapper
        testId="secure-vault"
        title="Your Secure Vault"
        description={"Everything you earn on HiBFF lives here — your Trophies and badges from completed real-world challenges. Nothing gets lost, and only YOU can see the details. Watching your Vault grow proves the practice is paying off."}
      >
        <div className="animate-slideUp" style={{ animationDelay: '0.15s' }} data-testid="secure-vault-hero">
          <SecureVaultCard />
        </div>
      </InfoTooltipWrapper>
      )}

      {/* Simplified dashboard banner — pre-Challenge-10 (S-022) */}
      {onboardingStage.simplified_dashboard && onboardingStage.next_unlock_in > 0 && (
        <div className="mb-6 p-4 rounded-2xl flex items-center gap-3" style={{
          background: 'linear-gradient(135deg, rgba(245,131,31,0.08), rgba(123,79,216,0.08))',
          border: '1px solid #F5C99F'
        }} data-testid="onboarding-banner">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5831F' }}>
            <span className="text-white font-bold text-sm">{onboardingStage.challenges_completed}/{onboardingStage.threshold}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">Simplified for now — focus on what matters</p>
            <p className="text-xs text-gray-600 mt-0.5">
              Complete <strong>{onboardingStage.next_unlock_in} more</strong> {isAdultMode ? 'sessions' : 'challenges'} to unlock the full dashboard, custom widgets, and your first <strong>{isAdultMode ? 'Verifiable Skills Badge' : 'virtual HiBFF badge'}</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Welcome Card for new users */}
      {isNewUser && (
        <div className="mb-6">
          <WelcomeCard />
        </div>
      )}

      {/* Profile Completion (full width — Quick Actions retired: the hero
          practice buttons and My Trainers already cover those routes) */}
      <div className="mb-6">
        <InfoTooltipWrapper
          testId="profile-completion"
          title="Profile Completion"
          description={"Shows how much of your HiBFF profile you've filled in. A complete profile helps your AvA give you better, more personalised practice — and unlocks bonus content along the way."}
          marginBottom=""
        >
          <ProfileCompletionBar />
        </InfoTooltipWrapper>
      </div>

      {/* Milestone Celebration Overlay */}
      <MilestoneCelebration />

      {/* Achievement Unlocked — confetti celebration system */}
      <AchievementUnlocked />

      {/* Engagement Row — Streak + Fortune Cookie */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <InfoTooltipWrapper
          testId="streak"
          title="Daily Streak"
          description={"Every day you show up for a practice session, your streak grows. Streaks are the single strongest predictor of confidence gains on HiBFF — miss a day and you lose it, so protect the chain."}
          marginBottom=""
        >
          <StreakCard />
        </InfoTooltipWrapper>
        <InfoTooltipWrapper
          testId="fortune-cookie"
          title="Today's Insight"
          description={"One small mind-shift or reframing tip, refreshed daily. Read it, screenshot it, or send it to a friend — a two-second nudge that adds up to a very different you over a month."}
          marginBottom=""
        >
          <FortuneCookie />
        </InfoTooltipWrapper>
      </div>

      {/* Jun 2026 reorg — the teen's KEY METRICS (Readiness + Mood) sit
          directly under the streak row, mirroring the Command Centre's
          "business metrics at the top" structure. Still gated by S-022. */}
      {!onboardingStage.simplified_dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <ReadinessScoreCard />
          <MoodInsightsCard />
        </div>
      )}

      {/* Sprint 207X — Section Header with Customise Cards pill moved DOWN
          so it sits directly above the customisable masonry grid (My Trainers,
          Mood Tracker, Zen Master, Today's Challenge, etc.). Previously it
          was anchored to the very top of the dashboard which detached it
          from the cards it actually controls.
          Sprint 207Y — Now `sticky` so it pins to the viewport top while the
          founder scrolls through the masonry — they can hit Customise Cards
          from any card row without scrolling back up. */}
      <div
        className="sticky top-4 z-20 flex items-center justify-between mb-4 px-3 py-2 rounded-xl bg-white/90 backdrop-blur shadow-sm border border-gray-100 animate-fadeIn gap-2"
        style={{ animationDelay: '0.1s' }}
        data-testid="dashboard-section-header"
      >
        <h2 className="text-base sm:text-xl font-bold text-gray-900 truncate min-w-0">Your Dashboard</h2>
        <button
          onClick={() => setShowAddPanel(true)}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-full transition-all hover:shadow-md flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #F5831F 0%, #7B4FD8 100%)',
            color: '#fff',
            boxShadow: '0 4px 14px rgba(123, 79, 216, 0.25)',
          }}
          data-testid="customize-dashboard-btn"
          aria-label="Customise dashboard cards"
        >
          <SlidersHorizontal size={16} />
          <span className="hidden sm:inline">Customise Cards</span>
        </button>
      </div>

      {/* Sprint 207g — Layout-stable masonry engine.
          Both FIXED_CARDS and user-enabled OPTIONAL_CARDS render through a
          single CSS-columns container with `column-fill: balance` +
          `break-inside: avoid` so the browser auto-balances column heights.
          Toggling a card on/off triggers an instant repack with no dead
          whitespace. Replaces the prior `i % 3` round-robin which trapped
          cards in fixed columns and left holes when any card was hidden. */}
      {isAdultAccount && (
        <div className="flex items-center justify-end gap-2 mb-3" data-testid="adult-challenges-toggle-row">
          <span className="text-xs font-medium text-gray-500">Weekly challenges</span>
          <button
            onClick={toggleChallenges}
            data-testid="adult-challenges-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${challengesOptIn ? 'bg-teal-600' : 'bg-gray-300'}`}
            aria-label="Toggle weekly challenges"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${challengesOptIn ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      )}
      <div className="dashboard-masonry" data-testid="dashboard-masonry">
        {/* FIXED — these always render first so the order is deterministic
            for the explainer-video recording (My Trainers → Today's Challenge
            → Recent Conversations → Mood Tracker). */}
        {FIXED_CARDS.map((card, idx) => {
          if (challengesOff && CHALLENGE_CARD_IDS.includes(card.id)) return null;
          const CardComponent = card.component;
          return (
            <div
              key={`fixed-${card.id}`}
              className="dashboard-masonry-item animate-slideUp"
              style={{ animationDelay: `${idx * 0.06}s` }}
              data-testid={`fixed-card-${card.id}`}
            >
              <CardComponent />
            </div>
          );
        })}

        {/* OPTIONAL — driven by the visibility dictionary. Iterate the
            REGISTRY (not the dictionary) so the rendered order is always
            the canonical registry order, regardless of when the user
            toggled each card. Hidden cards (visibility=false) are skipped. */}
        {Object.entries(OPTIONAL_CARDS).map(([cardId, config], idx) => {
          if (!cardVisibility[cardId]) return null;
          if (challengesOff && CHALLENGE_CARD_IDS.includes(cardId)) return null;
          const CardComponent = config.component;
          return (
            <div
              key={`opt-${cardId}`}
              className="dashboard-masonry-item animate-slideUp relative group"
              style={{ animationDelay: `${(FIXED_CARDS.length + idx) * 0.05}s` }}
              data-testid={`optional-card-${cardId}`}
            >
              {/* Hover-reveal info affordance (Feb 2026). Renders the
                  card's registry `description` in a small popover on
                  hover so teens instantly understand what each tile
                  does without opening the Customise modal. Sits to
                  the LEFT of the X so it never collides with dismissal. */}
              {config.description && (
                <div className="absolute -top-2 right-6 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="relative group/info">
                    <button
                      type="button"
                      className="p-1 bg-purple-600 text-white rounded-full shadow-md flex items-center justify-center hover:bg-purple-700"
                      aria-label={`About ${config.title}`}
                      data-testid={`info-card-${cardId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Info size={12} />
                    </button>
                    <div
                      className="absolute right-0 top-full mt-1 w-64 rounded-lg bg-gray-900 text-white text-xs leading-snug p-3 shadow-xl opacity-0 group-hover/info:opacity-100 group-focus-within/info:opacity-100 transition-opacity pointer-events-none z-20"
                      role="tooltip"
                      data-testid={`info-tooltip-${cardId}`}
                    >
                      <div className="font-bold text-purple-300 mb-1">{config.title}</div>
                      <div className="text-gray-100">{config.description}</div>
                    </div>
                  </div>
                </div>
              )}
              <button
                onClick={() => hideCardFromX(cardId)}
                className="absolute -top-2 -right-2 z-10 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                data-testid={`remove-card-${cardId}`}
                title="Hide from dashboard — bring it back via Customise Cards"
              >
                <X size={14} />
              </button>
              <CardComponent />
            </div>
          );
        })}
      </div>

      {/* Jun 2026 reorg — weekly review + lighter engagement extras now sit
          BELOW the core masonry (Trainers / Today's Challenge), so the first
          screenful is: streak → key metrics → core actions. */}
      <InfoTooltipWrapper
        testId="weekly-wrap-up"
        title="Practices in Review"
        description={"Your last 7 days at a glance — sessions completed, streak days, XP earned, and the challenges you crushed. Use it to spot your patterns and pick where to double down next week."}
      >
        <WeeklyWrapUp />
      </InfoTooltipWrapper>

      {/* Gold Champion marquee — slim pill announcing the most recent Gold
          Proactive Champion. Auto-hides 48h after the award drop. */}
      <GoldChampionMarquee />

      {/* Post-unlock engagement extras (Challenge ≥ 10, S-022) */}
      {!onboardingStage.simplified_dashboard && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <DailyLoginReward />
            <StreakFreezeCard />
            <SessionReminderCard />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <FriendChallengeCard />
            <AvaMissesYouCard />
          </div>
          <div className="mb-6">
            <AffirmationCard />
          </div>
        </>
      )}

      {/* Spacer for bottom breathing room */}
      <div className="h-8" />

      {/* Feb 2026 — One-shot dashboard walkthrough. Fires the first time
          a teen sees the un-simplified dashboard, then never again
          (localStorage `hibff_dashboard_tour_v1_done`). Uses the same
          `OPTIONAL_CARDS` registry as the hover tooltips so descriptions
          stay in sync. Any of Skip/Done/scrim-click ends the tour.
          `?tour=1` in the URL forces the tour on for demos/QA. */}
      <DashboardCardTour
        enabled={
          // Never fire while the entry cover is still up, and never stack on
          // top of the 5-step Welcome Tour — sequence, don't compete.
          coverRevealed
          && !showWelcomeTour
          && (!onboardingStage.simplified_dashboard
            || new URLSearchParams(window.location.search).get('tour') === '1')
        }
        cardOrder={Object.keys(OPTIONAL_CARDS).filter((id) => cardVisibility[id])}
        cardMeta={Object.fromEntries(
          Object.entries(OPTIONAL_CARDS).map(([id, cfg]) => [id, { title: cfg.title, description: cfg.description }]),
        )}
      />

      {/* Add Cards Panel */}
      {showAddPanel && (
        <div className="fixed inset-0 z-50" data-testid="add-cards-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowAddPanel(false)} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-gray-900">Customise Cards</h2>
              <button onClick={() => setShowAddPanel(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <p className="text-sm text-gray-500 mb-4">Pick the cards you want on your dashboard. Add or remove anything, anytime.</p>
              <div className="space-y-2">
                {/* Sprint 207h — modal iterates the FULL OPTIONAL_CARDS registry
                    so every registered card is always visible here, regardless
                    of current visibility state. Hidden cards render with the
                    slashed-eye icon and can be re-enabled with a single tap. */}
                {Object.entries(OPTIONAL_CARDS).map(([cardId, config]) => {
                  const isEnabled = Boolean(cardVisibility[cardId]);
                  return (
                    <button
                      key={cardId}
                      onClick={() => toggleOptionalCard(cardId)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${
                        isEnabled
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      data-testid={`toggle-card-${cardId}`}
                      aria-pressed={isEnabled}
                    >
                      <div className="min-w-0">
                        <p className={`font-semibold text-sm ${isEnabled ? 'text-blue-700' : 'text-gray-900'}`}>{config.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{config.description}</p>
                      </div>
                      <div className={`flex-shrink-0 ml-3 p-1.5 rounded-lg ${isEnabled ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {isEnabled ? <Eye size={16} /> : <EyeOff size={16} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowAddPanel(false)}
                className="w-full py-2.5 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-11 h-11 bg-gray-900 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-700 transition-all z-40"
          data-testid="scroll-to-top"
          aria-label="Scroll to top"
        >
          <ArrowUp size={20} />
        </button>
      )}

      {/* Onboarding Tour for first-time users */}
      {/* OnboardingTour deferred — the new AvA First-Session Guide now
          handles the welcome introduction (mounted above HeroSection).
          The legacy sidebar-pointer tour stays available for future use
          if we want a follow-up "now look at these features" tour after
          the chat-style intro, but we don't fire it on every Dashboard
          mount any more (it was overlapping with FirstSessionGuide). */}
      {/* <OnboardingTour user={user} /> */}
      </div>
    </DashboardLayout>
    </MandatoryRegistrationGate>
  );
};

export default Dashboard;
